import { useEffect, useState } from 'react';
import { withTransaction } from '@elastic/apm-rum-react';
import {
  ButtonExport,
  ContainerHistoryControl, HeaderHistoryControl, Scroll,
} from './styles';
import { t } from 'i18next';
import { useStateVar } from '~/helpers/useStateVar';
import { toast } from 'react-toastify';
import { Loader, NewTable } from '~/components';
import { Helmet } from 'react-helmet';
import { AdminLayout, ClientLayout } from '../AdminLayout';
import { Moment } from 'moment-timezone';
import { FilterConfig, GenericFilter } from '~/components/FilterGeneric';
import { LIMIT_PAGE, OPTIONS_ECONOMY_MODE, TABLE_COLUMNS } from './constants';
import { apiCall, apiCallDownload } from '~/providers';
import RemoteControl from '../../../assets/img/remoteControl/remoteControl.svg';
import { LayoutIcon } from '@radix-ui/react-icons';
import { colors } from '~/styles/colors';
import { getUserProfile } from '~/helpers/userProfile';

type THistory = {
  ID: number
  UNIT_ID: number
  USER_ID: string
  UNIT_NAME: string
  CLIENT_ID: number
  CLIENT_NAME: string
  SMART_CONTROL_ACTION_DATE: string
  SMART_CONTROL_REASON_ID: number
  ENVIRONMENT_ID: number
  ENVIRONMENT_NAME: string
  MACHINE_ID: number
  MACHINE_NAME: string
  REASON: string
  MODE: string
  LAST_DEVICE_COMMAND: string
  BEFORE_LAST_DEVICE_COMMAND: string
  BEFORE_SETPOINT: number
  BEFORE_MODE: string
  CURRENT_SETPOINT: number
  CURRENT_MODE: string
  CURRENT_LAST_DEVICE_COMMAND: string
}

export function ControlHistory(): JSX.Element {
  const [state, render, setState] = useStateVar({
    isLoading: false,
    listFilter: [] as THistory[],
    selectedClientOpts: [] as any[],
    selectedUnitOpts: [] as any[],
    selectedReasonOpts: [] as any[],
    selectedChangeOpts: [] as any[],
    selectedUsersOpts: '' as string,
    selectedDateOpts: null as Moment|null,
    selectedEconomyModeOpts: '' as string,
    total: 0 as number,
    page: 1,
  });
  const [profile] = useState(getUserProfile);
  useEffect(() => {
    handleGetData({ page: state.page });
  }, [state.page]);

  const applyFilters = () => {
    handleGetData({ page: state.page });
    setState({ page: 1 });
  };

  const filters = [
    {
      type: 'multi-select',
      key: 'client',
      label: t('cliente'),
      placeholder: t('cliente'),
      multiple: true,
      isVisible: profile.permissions.isAdminSistema || profile.viewMultipleClients,
    },
    {
      type: 'multi-select',
      key: 'unit',
      label: 'Unidade',
      placeholder: 'Unidade',
      multiple: true,
      dependencies: ['client'],
      filterFunction: (options, selectedDeps) => {
        if (selectedDeps?.client && selectedDeps?.client?.length > 0) {
          return options.filter((item) => selectedDeps.client.includes(item.clientId?.toString()));
        }
        return options;
      },
      isVisible: true,
    },
    {
      type: 'date',
      key: 'date',
      label: t('data'),
      placeholder: 'dd/mm/aaaa',
      isVisible: true,
    },
    {
      type: 'searchable-select',
      key: 'user',
      label: t('usuario'),
      placeholder: t('digite'),
      isVisible: profile.permissions.isAdminSistema,
    },
    {
      type: 'multi-select',
      key: 'motivoAlteracao',
      label: t('motivo'),
      placeholder: t('motivo'),
      multiple: true,
      isVisible: true,
    },
    {
      type: 'multi-select',
      key: 'alteracao',
      label: t('alteracao'),
      placeholder: t('alteracao'),
      multiple: true,
      isVisible: true,
    },
    {
      type: 'select',
      key: 'modoEconomia',
      label: t('modoEconomia'),
      placeholder: t('modoEconomia'),
      options: OPTIONS_ECONOMY_MODE,
      isVisible: true,
    },
  ] as FilterConfig[];

  async function handleGetData({ page }) {
    try {
      setState({ isLoading: true });
      const historyPage = await apiCall('/remote-control/history', {
        UNIT_IDS: state.selectedUnitOpts,
        CLIENT_IDS: state.selectedClientOpts,
        REASONS: state.selectedReasonOpts,
        DATE: state.selectedDateOpts?.format('DD/MM/YYYY'),
        CHANGES: state.selectedChangeOpts,
        USER: state.selectedUsersOpts,
        ECONOMY_MODE: state.selectedEconomyModeOpts,
        limit: LIMIT_PAGE,
        offset: (page - 1) * LIMIT_PAGE,
      });
      state.listFilter = historyPage.history;
      state.total = historyPage.total || 0;
      render();
    } catch (err) {
      console.log(err);
      toast.error('Não foi possível carregar os dados');
    }
    state.isLoading = false;
    render();
  }

  const updateCheckInformations = (topic: string, newValues?: any, dates?: Moment | null) => {
    switch (topic) {
      case 'client':
        setState({ selectedClientOpts: newValues.map((item) => Number(item)) });
        break;
      case 'unit':
        setState({ selectedUnitOpts: newValues.map((item) => Number(item)) });
        break;
      case 'motivoAlteracao':
        setState({ selectedReasonOpts: newValues });
        break;
      case 'alteracao':
        setState({ selectedChangeOpts: newValues });
        break;
      case 'user':
        setState({ selectedUsersOpts: newValues });
        break;
      case 'date':
        setState({ selectedDateOpts: dates });
        break;
      case 'modoEconomia':
        setState({ selectedEconomyModeOpts: newValues });
        break;
      default:
        break;
    }
    render();
  };

  async function exportRemoteControlHistory() {
    toast.success(t('mensagemDeExportacaoIniciada'));
    try {
      const resp = await apiCallDownload('/remote-control/history-export', {
        UNIT_IDS: state.selectedUnitOpts,
        CLIENT_IDS: state.selectedClientOpts,
        REASONS: state.selectedReasonOpts,
        DATE: state.selectedDateOpts?.format('DD/MM/YYYY'),
        CHANGES: state.selectedChangeOpts,
        USER: state.selectedUsersOpts,
        ECONOMY_MODE: state.selectedEconomyModeOpts,
      });
      if (resp.data.type === 'application/json') {
        const msg = await resp.data.text();
        throw new Error(msg);
      }
      const fallback = `${t('historico')}.xlsx`;
      const filenameHdr = resp.headers.filename as string | undefined;
      const filename = filenameHdr ?? fallback;

      const blob = resp.data;
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      setTimeout(() => window.URL.revokeObjectURL(url), 1000);

      toast.success(t('sucessoExportacao'));
    } catch (err) {
      console.error(err);
      toast.error(t('erroAoExportar'));
    }
  }
  const TABLE_FILTERED = (profile.permissions.isAdminSistema || profile.viewMultipleClients) ? TABLE_COLUMNS : TABLE_COLUMNS.filter((item) => item.accessor !== 'CLIENT_NAME');

  return (
    <ContainerHistoryControl>
      <Helmet>
        <title>
          Diel Energia -
          {' '}
          {t('historicoControle')}
        </title>
      </Helmet>
      {
        !profile.permissions.isAdminSistema ? (
          <div style={{ marginBottom: 10 }}>
            <ClientLayout />
          </div>
        ) : (
          <AdminLayout />
        )
      }
      <HeaderHistoryControl>
        <img
          src={RemoteControl}
          alt="remote-control-icon"
        />
        <h1>{t('historicoAlteracoesControleInteligente')}</h1>
      </HeaderHistoryControl>
      <GenericFilter
        state={state}
        onApply={applyFilters}
        updateInfos={updateCheckInformations}
        filters={filters}
      />
      <div
        style={{
          display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', alignItems: 'center',
        }}
      >
        <h3 style={{ fontSize: 12, color: colors.text['soft-400'], fontWeight: 400 }}>
          {t('encontrada')}
          {' '}
          {state.total}
          {' '}
          {t('alteracoesNoControleInteligente')}
        </h3>
        {
          state.listFilter.length > 0 && (
            <ButtonExport onClick={exportRemoteControlHistory}>
              <LayoutIcon style={{ marginRight: '5px' }} />
              {t('exportarPlanilha')}
            </ButtonExport>
          )
        }
      </div>
      {
        state.isLoading ? (
          <Loader />
        ) : (
          <Scroll>
            <NewTable
              noSearchBar
              data={state.listFilter}
              columns={TABLE_FILTERED}
              pageSize={LIMIT_PAGE}
              dataForPage
              page={state.page}
              maxPages={state.total}
              styledPagination
              changePage={(page) => setState({ page })}
              variant="striped"
            />
          </Scroll>
        )
      }
    </ContainerHistoryControl>
  );
}

export default withTransaction('ControlHistory', 'component')(ControlHistory);
